import { forwardRef, useCallback, useMemo, useState } from 'react';
import type { Ref } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { IconButtonSize } from '@opendoor/tokens-studio/dist/bricks-next/types/Components';
import type { Color } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { isWeb, styled, Text as TamaguiText, View } from '@tamagui/core';
import type { GetProps, TamaguiElement } from '@tamagui/core';

import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Close,
  Download,
  Edit,
  FavoritedFilled,
  FavoriteUnfilled,
  Filter,
  Menu,
  NearMe,
  PasswordHide,
  Share,
} from '../../__generated__/icons';
import { getStateTokenValue } from '../../helpers/tokens';
import { createAnchorOrButtonFrame } from '../shared/anchorOrButton';
import type { AnchorOrButtonProps } from '../shared/anchorOrButton';

type IconComponentType = React.ComponentType<{
  /** Size of the icon in pixels */
  size?: 20 | 24;
  /** Color of the icon */
  color?: Color;
  /** Aria label for the icon */
  'aria-label'?: string;
}>;

const { iconButton: iconButtonTokens } = tokens;

export type IconButtonVariant = Exclude<
  keyof typeof iconButtonTokens.color.icon.background,
  'state'
>;

const sharedFrameConfig = {
  name: 'IconButton',

  userSelect: 'none',
  cursor: 'pointer',
  borderStyle: 'solid',
  borderWidth: iconButtonTokens.borderWidth.default,
  borderRadius: iconButtonTokens.cornerRadius.default,
  alignItems: 'center',
  justifyContent: 'center',

  // Mobile safari sometimes adds a default horizontal padding to buttons
  padding: 0,

  animation: 'linear',
  animateOnly: isWeb
    ? [
        'transform',
        'background-color',
        'border-color',
        'border-top-color',
        'border-left-color',
        'border-bottom-color',
        'border-right-color',
      ]
    : [
        'transform',
        'backgroundColor',
        'borderColor',
        'borderTopColor',
        'borderLeftColor',
        'borderBottomColor',
        'borderRightColor',
      ],

  focusVisibleStyle: {
    borderColor: iconButtonTokens.color.icon.border.state.focused.default,
  },

  pressStyle: {
    scale: getStateTokenValue(iconButtonTokens.state.pressedScale),
  },

  variants: {
    variant: (
      variant: IconButtonVariant,
      {
        props,
      }: {
        /** Extra props */
        props: {
          /** Whether the button is disabled */
          disabled?: boolean;
        };
      },
    ) => {
      return props.disabled
        ? {
            backgroundColor: iconButtonTokens.color.icon.background.state.inactive[variant],
            borderColor: iconButtonTokens.color.icon.border.state.inactive[variant],
          }
        : {
            backgroundColor: iconButtonTokens.color.icon.background[variant],
            borderColor: iconButtonTokens.color.icon.border[variant],
            hoverStyle: {
              backgroundColor: iconButtonTokens.color.icon.background.state.hover[variant],
              borderColor: iconButtonTokens.color.icon.border.state.hover[variant],
            },
          };
    },

    frameSize: (size: IconButtonSize) => ({
      width: iconButtonTokens.size[size],
      height: iconButtonTokens.size[size],
      ...(size === 'small'
        ? ({
            backgroundColor: '$transparent',
            borderColor: '$transparent',
            hoverStyle: {
              borderColor: '$transparent',
            },
          } as const)
        : {}),
    }),

    disabled: {
      true: {
        pointerEvents: 'none',
      },
    },
  },
} as const;

const AnchorFrame = styled(TamaguiText, {
  ...sharedFrameConfig,
  tag: 'a',
  accessibilityRole: 'link',
  textDecorationLine: 'none',
  display: 'flex',
  position: 'relative',
});

type AnchorFrameProps = GetProps<typeof AnchorFrame>;

const ButtonFrame = styled(View, {
  ...sharedFrameConfig,
  tag: 'button',
  accessibilityRole: 'button',
});

type ButtonFrameProps = GetProps<typeof ButtonFrame>;

const AnchorOrButtonFrame = createAnchorOrButtonFrame<AnchorFrameProps | ButtonFrameProps>({
  AnchorFrame,
  ButtonFrame,
});

type AnchorOrButtonFrameProps = GetProps<typeof AnchorOrButtonFrame>;

export type IconButtonProps = Omit<AnchorOrButtonFrameProps, 'children' | 'frameSize' | 'tag'> & {
  /** IconButton color variant */
  variant?: IconButtonVariant;
  /** IconButton size */
  size: IconButtonSize;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Type of the HTML button */
  type?: 'button' | 'submit' | 'reset';
  /** Value associated with the HTML button */
  value?: string;
  /**
   * Icon to render inside the button
   */
  icon: {
    /**
     * The icon component to render inside the button.
     * Can be either a Bricks icon or an external icon wrapped with adaptExternalIcon
     */
    Component: IconComponentType;
    /**
     * The icon label (for screen readers)
     */
    label: string;
    /**
     * Optional size of the icon. Defaults to 20 for small and medium IconButtons
     * and 24 for large IconButtons.
     */
    size?: 20 | 24;
    /**
     * Optional color of the icon
     */
    color?: Color;
  };
};

/**
 * The IconButton component is a pressable element whose visible content
 * consists only of an icon.
 *
 * Pass the `icon` prop to specify details about the icon. `icon.Component`
 * tells the IconButton which icon to render inside the button. You'll also
 * need to pass `icon.label` to provide a screen-reader-friendly label for
 * the icon.
 *
 * Pass the `size` prop to control the size of the icon. Pass `variant` if you
 * need to change the color of the button.
 *
 * Like Button, IconButton accepts an `href` prop that lets you use the
 * component for URL-based navigation. Refer to the Button component docs for
 * more information. Also refer to the Button docs for information on analytics
 * props.
 *
 * A number of icons are available as pre-built icon button components. For
 * example, if you need a button with a Close icon, import the CloseButton
 * component instead of IconButton. Refer to the docs for all available
 * pre-built icon buttons. A special icon button worth noting is the
 * FavoriteButton — it handles favorite/unfavorite state for you.
 */
export const IconButton = forwardRef(function IconButton(
  { size, disabled, icon, variant = 'default', ...rest }: IconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  const iconColor = disabled
    ? iconButtonTokens.color.icon.content.state.inactive.default
    : (icon.color ?? iconButtonTokens.color.icon.content[variant]);

  const iconSize = icon.size ?? (size === 'large' ? 24 : 20);

  const renderIcon = () => {
    return <icon.Component aria-label={icon.label} color={iconColor} size={iconSize} />;
  };

  return (
    <AnchorOrButtonFrame
      disabled={disabled}
      aria-disabled={disabled}
      focusable={!disabled}
      {...rest}
      variant={variant}
      frameSize={size}
      ref={ref}
    >
      {renderIcon()}
    </AnchorOrButtonFrame>
  );
});

type SpecialIconButtonProps = Omit<IconButtonProps, 'icon'> & {
  /** Optional prop in case you need to override the default icon label */
  iconLabel?: IconButtonProps['icon']['label'];
  /** Optional prop in case you need to override the default icon size */
  iconSize?: IconButtonProps['icon']['size'];
};

type IconButtonPressHandler = NonNullable<AnchorOrButtonProps['onPress']>;
type IconButtonPressEvent = Parameters<IconButtonPressHandler>[0];

/** Icon button with a Close icon */
export const CloseButton = forwardRef(function CloseButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: Close, label: iconLabel ?? 'close', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a Menu icon */
export const MenuButton = forwardRef(function MenuButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: Menu, label: iconLabel ?? 'menu', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a ChevronLeft icon */
export const ChevronBackButton = forwardRef(function ChevronBackButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: ChevronLeft, label: iconLabel ?? 'back', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a ChevronRight icon */
export const ChevronForwardButton = forwardRef(function ChevronForwardButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: ChevronRight, label: iconLabel ?? 'forward', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a ChevronUp icon */
export const ChevronUpButton = forwardRef(function ChevronUpIconButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: ChevronUp, label: iconLabel ?? 'up', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a ChevronUp icon */
export const ChevronDownButton = forwardRef(function ChevronDownIconButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: ChevronDown, label: iconLabel ?? 'down', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with an Edit icon */
export const EditButton = forwardRef(function EditButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: Edit, label: iconLabel ?? 'edit', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a Download icon */
export const DownloadButton = forwardRef(function DownloadButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{
        Component: Download,
        label: iconLabel ?? 'download',
        // Medium size uses 24px icon
        size: iconSize ?? (props.size === 'small' ? 20 : 24),
      }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a Filter icon */
export const FilterButton = forwardRef(function FilterButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: Filter, label: iconLabel ?? 'filter', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/**
 * Icon button with a Favorite icon. The button manages its own
 * favorite/unfavorite state by default. If you'd rather manage the state
 * yourself (e.g., to handle an error response from a server), pass in the
 * `isFavorited` prop and that will override the component's internal state.
 */
export const FavoriteButton = forwardRef(function FavoriteButton(
  {
    iconLabel,
    iconSize,
    defaultFavorited,
    isFavorited,
    onPress,
    ...props
  }: SpecialIconButtonProps & {
    /** Whether the button starts in the favorited state */
    defaultFavorited?: boolean;
    /**
     * Whether the button is currently in the favorited state.
     * Only pass this if you want to override the component's internal state
     */
    isFavorited?: boolean;
  },
  ref: Ref<TamaguiElement>,
) {
  const [internalIsFavorited, setInternalIsFavorited] = useState(defaultFavorited ?? false);
  const isControlledComponent = typeof isFavorited !== 'undefined';
  const favorited = isControlledComponent ? isFavorited : internalIsFavorited;
  const onPressHandler = onPress as AnchorOrButtonProps['onPress'];

  const handlePress = useCallback(
    (e: IconButtonPressEvent) => {
      !isControlledComponent && setInternalIsFavorited((prev) => !prev);
      if (onPressHandler) {
        return onPressHandler(e);
      }
      return undefined;
    },
    [isControlledComponent, onPressHandler],
  );

  const icon = useMemo(() => {
    return favorited
      ? {
          Component: FavoritedFilled,
          label: iconLabel ?? 'unfavorite',
          size: iconSize,
          color: iconButtonTokens.color.icon.content.favorite,
        }
      : {
          Component: FavoriteUnfilled,
          label: iconLabel ?? 'favorite',
          size: iconSize,
        };
  }, [favorited, iconLabel, iconSize]);

  return (
    <IconButton
      icon={icon}
      value={favorited ? 'false' : 'true'}
      onPress={handlePress}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a NearMe icon */
export const NearMeButton = forwardRef(function NearMeButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: NearMe, label: iconLabel ?? 'near me', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a Hide icon */
export const HideButton = forwardRef(function HideButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{ Component: PasswordHide, label: iconLabel ?? 'hide', size: iconSize }}
      {...props}
      ref={ref}
    />
  );
});

/** Icon button with a Share icon */
export const ShareButton = forwardRef(function ShareButton(
  { iconLabel, iconSize, ...props }: SpecialIconButtonProps,
  ref: Ref<TamaguiElement>,
) {
  return (
    <IconButton
      icon={{
        Component: Share,
        label: iconLabel ?? 'share',
        // Medium size uses 24px icon
        size: iconSize ?? (props.size === 'small' ? 20 : 24),
      }}
      {...props}
      ref={ref}
    />
  );
});
